import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, AlertCircle, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao fazer login';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Tela inicial com opções
  if (!showLoginForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900 px-4">
        <Card className="w-full max-w-md shadow-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <CardHeader className="space-y-4 text-center pb-2">
            <div className="mx-auto">
              <img 
                src="/logo-login.png" 
                alt="Analytica Studies" 
                className="w-24 h-24 mx-auto"
              />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Analytica Studies</CardTitle>
              <CardDescription className="text-base mt-2 text-slate-600 dark:text-slate-400">
                Acompanhe seu progresso na residência médica
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <Button
              onClick={() => setShowLoginForm(true)}
              className="w-full h-12 text-base gap-2"
              data-testid="goto-login-btn"
            >
              <LogIn className="w-5 h-5" />
              Entrar
            </Button>
            <Button
              onClick={() => navigate('/register')}
              variant="outline"
              className="w-full h-12 text-base gap-2"
              data-testid="goto-register-btn"
            >
              <UserPlus className="w-5 h-5" />
              Criar Conta
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900 px-4">
      <Card className="w-full max-w-md shadow-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Bem-vindo de volta</CardTitle>
            <CardDescription className="text-base mt-2 text-slate-600 dark:text-slate-400">
              Acesse sua conta para continuar seus estudos
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-lg" data-testid="login-error-message">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Senha</Label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="forgot-password-link">
                  Esqueceu a senha?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Não tem uma conta?{' '}
              <Link to="/register" className="text-brand hover:underline font-medium" data-testid="register-link">
                Criar conta
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
